import { createClerkSupabaseClient } from '../lib/supabase.js';
import { supabase as supabaseBase } from './supabase/supabaseClient.js';
import { getErrorSummary, logError } from '../lib/utils.js';

// Función para crear cliente autenticado con Clerk
const createAuthenticatedClient = async () => {
  // Obtener la sesión de Clerk desde el contexto global
  const session = window.Clerk?.session;
  
  if (!session) {
    throw new Error('Usuario no autenticado con Clerk');
  }
  
  return createClerkSupabaseClient(session);
};

const supabase = supabaseBase;

console.log('✅ Cliente Supabase inicializado correctamente');

// Cliente de Supabase simplificado sin autenticación personalizada
// Usando solo el cliente base para evitar errores JWT

// Verificar que la instancia de supabase esté disponible
if (!supabase) {
  const error = new Error('La instancia de Supabase no se pudo crear');
  console.error('❌ Error crítico:', error.message);
  throw error;
}

// Función helper para validar la instancia de Supabase
const validateSupabaseInstance = () => {
  if (!supabase) {
    const error = new Error('La instancia de Supabase no está disponible. Verifique la configuración.');
    console.error('❌ validateSupabaseInstance - Error:', error.message);
    throw error;
  }
  return true;
};

// Función de prueba para verificar conexión directa a Supabase
export const testSupabaseConnection = async () => {
  try {
    console.log('🧪 testSupabaseConnection - Iniciando prueba de conexión...');
    
    // Verificar variables de entorno en tiempo de ejecución
    console.log('🔍 Variables de entorno en tiempo de ejecución:');
    console.log('  - URL:', process.env.REACT_APP_SUPABASE_URL);
    console.log('  - KEY:', process.env.REACT_APP_SUPABASE_KEY ? 'Definida' : 'NO DEFINIDA');
    
    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_KEY) {
      return {
        success: false,
        error: 'Variables de entorno no disponibles en tiempo de ejecución',
        details: {
          url: process.env.REACT_APP_SUPABASE_URL ? 'OK' : 'FALTA',
          key: process.env.REACT_APP_SUPABASE_KEY ? 'OK' : 'FALTA'
        }
      };
    }
    
    // Prueba simple: obtener información de la tabla planes
    const { data, error } = await supabase
      .from('planes')
      .select('id, nombre')
      .limit(1);
    
    if (error) {
      logError('❌ testSupabaseConnection - Error en consulta:', error, { ignoreAbort: true });
      return {
        success: false,
        error: 'Error en consulta a Supabase',
        details: getErrorSummary(error)
      };
    }
    
    console.log('✅ testSupabaseConnection - Conexión exitosa');
    return {
      success: true,
      message: 'Conexión a Supabase exitosa',
      data: data
    };
    
  } catch (error) {
    logError('❌ testSupabaseConnection - Error inesperado:', error, { ignoreAbort: true });
    return {
      success: false,
      error: 'Error inesperado en prueba de conexión',
      details: getErrorSummary(error)
    };
  }
};

// IDs de producto que tienen al menos un plan colectiva activo — usado para
// filtrar el selector de producto del wizard de póliza colectiva (una
// póliza colectiva solo puede referenciar un plan tipo_emision='colectiva').
export const getProductoIdsConPlanColectivo = async (client = supabase) => {
  try {
    const { data, error } = await client
      .from('planes')
      .select('producto_id')
      .eq('tipo_emision', 'colectiva')
      .eq('activo', true);

    if (error) {
      console.error('Error al obtener productos con plan colectivo:', error);
      throw error;
    }

    return new Set((data || []).map((p) => p.producto_id));
  } catch (error) {
    console.error('Error al obtener productos con plan colectivo:', error);
    throw error;
  }
};

// Obtener todos los planes
export const getPlanes = async (client = supabase) => {
  try {
    validateSupabaseInstance();
    console.log('📋 getPlanes - Cliente:', client === supabase ? 'base/anon' : 'autenticado');

    const { data, error } = await client
      .from('planes')
      .select(`
        id,
        nombre,
        activo,
        archivado,
        es_complementario,
        planes_base,
        condicionado_archivo_id,
        cuadro_coberturas_archivo_id,
        certcondicionado_archivo_id,
        tipo_emision,
        producto:producto(id, nombre, descripcion, activo, compania_id, compania:companias(id, nombre_corto)),
        precios:precios_base(precio_uf),
        descuentos_base(porcentaje, acumulable, tipo_carga)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error al obtener planes:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error al obtener planes:', error);
    throw error;
  }
};

// Obtener plan por ID
export const getPlanById = async (id) => {
  try {
    validateSupabaseInstance();
    console.log('📋 getPlanById - Usando cliente base para ID:', id);
    
    const { data, error } = await supabase
      .from('planes')
      .select(`
        *,
        producto:producto(id, nombre, descripcion),
        precios:precios_base(*),
        descuentos_base(*)
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error al obtener plan:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error al obtener plan:', error);
    throw error;
  }
};

// Crear nuevo plan
export const createPlan = async (planData) => {
  try {
    validateSupabaseInstance();
    console.log('📋 createPlan - Usando cliente base de Supabase');
    
    const { data, error } = await supabase
      .from('planes')
      .insert([
        {
          nombre: planData.nombre,
          descripcion: planData.descripcion,
          descripcion_json: planData.descripcion_json || null,
          producto_id: planData.producto_id,
          activo: planData.activo !== undefined ? planData.activo : true,
          archivado: planData.archivado !== undefined ? planData.archivado : false,
          solo_empresa: planData.solo_empresa !== undefined ? planData.solo_empresa : false,
          image_url: planData.image_url || null,
          // El insert es un whitelist: una columna que no esté acá se pierde sin
          // avisar. `is_url`/`url` van explícitas para que un plan tipo URL creado
          // desde cero no nazca apagado.
          is_url: planData.is_url !== undefined ? planData.is_url : false,
          url: planData.url || null,
          tipo_emision: planData.tipo_emision || null,
          moneda: planData.moneda || 'UF',
          subscription: planData.subscription !== undefined ? planData.subscription : true,
          contado: planData.contado !== undefined ? planData.contado : true
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Error al crear plan:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error al crear plan:', error);
    throw error;
  }
};

// Actualizar plan - SOLO CAMPOS MODIFICADOS
export const updatePlan = async (id, planData, client) => {
  try {
    console.log('🔄 updatePlan - Iniciando actualización con Clerk auth');
    console.log('📋 updatePlan - ID del plan:', id);
    console.log('📋 updatePlan - Datos a actualizar:', planData);

    // Cliente autenticado: el provisto por el caller (Clerk) o fallback global
    const supabase = client || (await createAuthenticatedClient());
    
    // Realizar la actualización
    const { data, error } = await supabase
      .from('planes')
      .update(planData)
      .eq('id', id)
      .select();
    
    if (error) {
      console.error('❌ updatePlan - Error de Supabase:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.warn('⚠️ updatePlan - Supabase no retornó datos, verificando...');
      
      // Consulta de verificación
      const { data: verifyData, error: verifyError } = await supabase
        .from('planes')
        .select('*')
        .eq('id', id)
        .single();
      
      if (verifyError) {
        console.error('❌ updatePlan - Error en verificación:', verifyError);
        throw verifyError;
      }
      
      if (!verifyData) {
        throw new Error('Plan no encontrado después de la actualización');
      }
      
      console.log('✅ updatePlan - Verificación exitosa:', verifyData);
      return verifyData;
    }
    
    console.log('✅ updatePlan - Actualización exitosa:', data[0]);
    return data[0];
    
  } catch (error) {
    console.error('❌ updatePlan - Error general:', error);
    throw error;
  }
};

// Eliminar plan
export const deletePlan = async (id, client = supabase) => {
  try {
    validateSupabaseInstance();
    console.log('📋 deletePlan - Eliminando plan ID:', id);

    const { data, error } = await client
      .from('planes')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error al eliminar plan:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error al eliminar plan:', error);
    throw error;
  }
};

// Estado activo de un producto puntual, por id. A diferencia de getProductos
// (que solo lista los activos, para el selector), este trae el producto exista
// o no esté activo — para validar antes de activar un plan que su producto no
// esté inactivo. Devuelve { id, nombre, activo } o null si no existe.
export const getProductoEstado = async (productoId, client = supabase) => {
  if (!productoId) return null;
  try {
    validateSupabaseInstance();
    const { data, error } = await client
      .from('producto')
      .select('id, nombre, activo')
      .eq('id', productoId)
      .maybeSingle();
    if (error) {
      console.error('Error al obtener estado del producto:', error);
      throw error;
    }
    return data || null;
  } catch (error) {
    console.error('Error al obtener estado del producto:', error);
    throw error;
  }
};

// Obtener productos para el selector
export const getProductos = async () => {
  try {
    validateSupabaseInstance();
    const { data, error } = await supabase
      .from('producto')
      .select('id, nombre, descripcion')
      .eq('activo', true)
      .order('nombre');

    if (error) {
      console.error('Error al obtener productos:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error al obtener productos:', error);
    throw error;
  }
};

// Obtener precios base para un plan
export const getPreciosBase = async (planId) => {
  try {
    validateSupabaseInstance();
    const { data, error } = await supabase
      .from('precios_base')
      .select('*')
      .eq('plan_id', planId)
      .order('created_at');

    if (error) {
      console.error('Error al obtener precios base:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error al obtener precios base:', error);
    throw error;
  }
};

// Obtener descuentos base para un plan
export const getDescuentosBase = async (planId) => {
  try {
    validateSupabaseInstance();
    const { data, error } = await supabase
      .from('descuentos_base')
      .select('*')
      .eq('plan_id', planId)
      .eq('activo', true)
      .order('created_at');

    if (error) {
      console.error('Error al obtener descuentos base:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error al obtener descuentos base:', error);
    throw error;
  }
};

// Crear precio base
export const createPrecioBase = async (precioData) => {
  try {
    validateSupabaseInstance();
    const { data, error } = await supabase
      .from('precios_base')
      .insert([precioData])
      .select()
      .single();

    if (error) {
      console.error('Error al crear precio base:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error al crear precio base:', error);
    throw error;
  }
};

// Crear descuento base
export const createDescuentoBase = async (descuentoData) => {
  try {
    validateSupabaseInstance();
    const { data, error } = await supabase
      .from('descuentos_base')
      .insert([descuentoData])
      .select()
      .single();

    if (error) {
      console.error('Error al crear descuento base:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error al crear descuento base:', error);
    throw error;
  }
};

// Función de diagnóstico para verificar existencia de plan
export const diagnosePlanExists = async (planId) => {
  try {
    validateSupabaseInstance();
    console.log('🔍 diagnosePlanExists - Iniciando diagnóstico para ID:', planId);
    
    // Validar que el ID no esté vacío
    if (!planId) {
      console.error('❌ diagnosePlanExists - ID del plan es requerido');
      return {
        exists: false,
        error: 'ID del plan es requerido',
        planId: planId
      };
    }

    console.log('📋 diagnosePlanExists - Usando cliente base de Supabase');

    // Consulta directa a la tabla planes
    console.log('🔍 diagnosePlanExists - Ejecutando consulta directa a tabla planes...');
    const { data, error, count } = await supabase
      .from('planes')
      .select('*', { count: 'exact' })
      .eq('id', planId);

    console.log('📊 diagnosePlanExists - Resultado de consulta:', {
      data: data,
      error: error,
      count: count,
      planId: planId
    });

    if (error) {
      console.error('❌ diagnosePlanExists - Error en consulta:', error);
      return {
        exists: false,
        error: error,
        planId: planId,
        queryExecuted: true
      };
    }

    const exists = data && data.length > 0;
    console.log(`${exists ? '✅' : '❌'} diagnosePlanExists - Plan ${exists ? 'ENCONTRADO' : 'NO ENCONTRADO'}`);
    
    if (exists) {
      console.log('📋 diagnosePlanExists - Datos del plan encontrado:', data[0]);
    }

    return {
      exists: exists,
      planData: exists ? data[0] : null,
      count: count,
      planId: planId,
      queryExecuted: true,
      timestamp: new Date().toISOString()
    };

  } catch (error) {
    console.error('❌ diagnosePlanExists - Error general:', error);
    return {
      exists: false,
      error: error,
      planId: planId,
      queryExecuted: false,
      timestamp: new Date().toISOString()
    };
  }
};

// Función de prueba temporal para diagnosticar el problema PGRST116
export const testDirectQuery = async (testId = 'f3ce859f-1fb2-4a9e-97c1-42d2bcfced7c') => {
  console.log('🔍 === INICIANDO DIAGNÓSTICO DIRECTO ===');
  
  try {
    validateSupabaseInstance();
    console.log('📋 testDirectQuery - Usando cliente base de Supabase');
    
    // 1. Consulta simple sin filtros para ver todos los planes
    console.log('📋 Paso 1: Consultando TODOS los planes...');
    const { data: allPlanes, error: allError } = await supabase
      .from('planes')
      .select('id, nombre, activo')
      .limit(10);
    
    if (allError) {
      console.error('❌ Error al consultar todos los planes:', allError);
    } else {
      console.log(`✅ Total de planes encontrados: ${allPlanes?.length || 0}`);
      console.log('📊 Primeros planes:', allPlanes?.slice(0, 3));
      
      // Verificar si nuestro ID específico está en la lista
      const planExists = allPlanes?.find(p => p.id === testId);
      console.log(`🎯 Plan con ID ${testId} ${planExists ? 'ENCONTRADO' : 'NO ENCONTRADO'} en la lista general`);
      if (planExists) {
        console.log('📝 Datos del plan encontrado:', planExists);
      }
    }
    
    // 2. Consulta específica con el ID problemático
    console.log(`\n🔎 Paso 2: Consultando plan específico con ID: ${testId}`);
    const { data: specificPlan, error: specificError } = await supabase
      .from('planes')
      .select('*')
      .eq('id', testId);
    
    if (specificError) {
      console.error('❌ Error en consulta específica:', specificError);
      console.error('❌ Código de error:', specificError.code);
      console.error('❌ Mensaje:', specificError.message);
      console.error('❌ Detalles:', specificError.details);
    } else {
      console.log(`✅ Resultado de consulta específica:`);
      console.log(`📊 Número de registros retornados: ${specificPlan?.length || 0}`);
      if (specificPlan && specificPlan.length > 0) {
        console.log('📝 Datos del plan específico:', specificPlan[0]);
      } else {
        console.log('⚠️ No se encontraron registros para el ID específico');
      }
    }
    
    // 3. Verificar permisos y configuración
    console.log('\n🔐 Paso 3: Verificando permisos y configuración...');
    
    // Verificar configuración de Supabase
    console.log('🔧 URL de Supabase:', supabase.supabaseUrl);
    console.log('🔑 Clave anon configurada:', supabase.supabaseKey ? 'SÍ' : 'NO');
    
    // Intentar una consulta de prueba con single()
    console.log(`\n🎯 Paso 4: Probando consulta con .single() (método que falla)...`);
    try {
      const { data: singleResult, error: singleError } = await supabase
        .from('planes')
        .select('*')
        .eq('id', testId)
        .single();
      
      if (singleError) {
        console.error('❌ Error con .single():', singleError);
        console.error('❌ Código:', singleError.code);
        console.error('❌ Mensaje:', singleError.message);
        console.error('❌ Detalles:', singleError.details);
      } else {
        console.log('✅ Éxito con .single():', singleResult);
      }
    } catch (singleException) {
      console.error('❌ Excepción con .single():', singleException);
    }
    
    // 5. Verificar permisos de tabla
    console.log('\n🔐 Paso 5: Verificando permisos de tabla...');
    try {
      const { data: permissionsTest, error: permissionsError } = await supabase
        .from('planes')
        .select('count')
        .limit(1);
      
      if (permissionsError) {
        console.error('❌ Error de permisos:', permissionsError);
      } else {
        console.log('✅ Permisos OK - Puede acceder a la tabla');
      }
    } catch (permError) {
      console.error('💥 Error al verificar permisos:', permError);
    }
    
    console.log('\n🏁 === DIAGNÓSTICO COMPLETADO ===');
    
    return {
      allPlanes: allPlanes?.length || 0,
      specificPlanFound: specificPlan?.length > 0,
      hasErrors: !!(allError || specificError)
    };
    
  } catch (error) {
    console.error('💥 Error general en diagnóstico:', error);
    throw error;
  }
};
